import React, { useEffect, useMemo } from 'react';
import defaultTo from 'lodash/defaultTo';
import isNil from 'lodash/isNil';
import cloneDeep from 'lodash/cloneDeep';
import { ApolloQueryResult } from '@apollo/client';
import {
  BillingFrequency,
  EntitlementDetails,
  LinkedDestinationEntitlement,
  Offering,
  PricingPlan,
  InvoiceGroup,
  FetchAvailablePricingPlansQuery,
  BillingStatus,
  Maybe,
} from '@css-xp/graphtypes/src/generated-types-new';
import { Currency, PricingPlanType } from '@atlassian/cssxp-ccp-types';
import { PricingPlanOption } from '@atlassian/cssxp-typescommon';
import {
  notActivePricingStatus,
  getUserTierOptions,
  getOfferingOptions,
  getPricingPlanOptions,
  getPricingPlanDescription,
  getDefaultPricingPlan,
} from '@atlassian/cssxp-utils-entitlement';
import {
  ManageSubscriptionPanel,
  OfferingOption,
  OnChangeFieldValue,
  UserTierOption,
  useFeatureToggle,
  FieldConfig,
} from '@css-xp/base';
import { ENABLE_ANNUAL_TERMS } from '@atlassian/cssxp-utils-generic';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import { isTrialEntitlement } from '../../../../entitlement-page-utility/entitlement-summary/entitlment-summary-utils';
import {
  FormState,
  AvailableOfferings,
  AvailablePricingPlans,
  AvailablePromotionsType,
  DefaultApiData,
  DefaultAPIResponse,
  RefetchLoadingState,
  TierValidationType,
  PartnerPromotionType,
} from './manage-entitlement-v2';
import {
  pickPromotionFields,
  getFormStateWithoutAddedPromotionFields,
  getDefaultAPIEntitlementData,
  fetchPricingPlans,
  evaluateOffering,
  getHiddenFieldConfig,
  isTierChangeAllowed,
} from './manage-entitlement-helper';
import './manage-entitlement-v2.scss';
import { getDisabledFieldConfig, isOptedForUpgradeTrial } from './manage-plan-helper';
import { FORM_NAMES } from './constants';

interface LinkedDestinationEntitlementWithDetails extends LinkedDestinationEntitlement {
  offeringOptions?: OfferingOption[];
  pricingPlanOptions?: PricingPlanOption[];
  userTierOptions?: UserTierOption[];
  isDisabled?: FieldConfig;
  isHidden?: FieldConfig;
}
interface Props {
  entitlement: EntitlementDetails;
  formState: FormState;
  availablePricingPlans: AvailablePricingPlans;
  availableOfferings: AvailableOfferings;
  linkedEntitlements: LinkedDestinationEntitlement[];
  filteredLinkedEntitlements: LinkedDestinationEntitlement[];
  isBillingCycleChanged: boolean;
  availablePromotionsData: AvailablePromotionsType;
  partnerPromotionData: PartnerPromotionType;
  setAvailablePromotions: (
    entitlementNumber: EntitlementDetails['entitlementNumber'],
    skipTrial: FormState['skipTrial']
  ) => void;
  handleCustomPromotionValueChange: (fieldValue: any) => void;
  updateFormState: (newState: FormState) => void;
  updateFormStateValues: (newState: FormState) => void;
  isAnnualBillingCycleChanged: boolean;
  currentInvoiceGroup: InvoiceGroup | undefined;
  updateDefaultApiData: (data: DefaultApiData) => void;
  updateAvailablePricingPlans: (data: AvailablePricingPlans) => void;
  defaultApiData: DefaultApiData;
  pricingEligibility: PricingPlanType | null;
  refetchLoadingState: RefetchLoadingState;
  updateRefetchLoadingState: (data: RefetchLoadingState) => void;
  setIsFormDirty: (data: null | boolean) => void;
  tierValidationState: TierValidationType;
  setTierValidationState: (data: TierValidationType) => void;
}

export const ManagePlanV2 = ({
  entitlement,
  formState,
  availablePricingPlans,
  availableOfferings,
  linkedEntitlements,
  filteredLinkedEntitlements,
  isBillingCycleChanged,
  availablePromotionsData,
  partnerPromotionData,
  handleCustomPromotionValueChange,
  setAvailablePromotions,
  updateFormState,
  updateFormStateValues,
  isAnnualBillingCycleChanged,
  currentInvoiceGroup,
  updateDefaultApiData,
  updateAvailablePricingPlans,
  defaultApiData,
  pricingEligibility,
  refetchLoadingState,
  updateRefetchLoadingState,
  setIsFormDirty,
  tierValidationState,
  setTierValidationState,
}: Props) => {
  const [isEnabled] = useFeatureToggle();
  const isAnnualTermEnabled = isEnabled(ENABLE_ANNUAL_TERMS);
  const entitlementDetails = entitlement;
  const isStatusPageProduct = isStatuspage(entitlement.productId);
  const isHidden = useMemo(
    () =>
      getHiddenFieldConfig({
        isBillingCycleChanged,
        entitlement,
        formState,
      }),
    [isBillingCycleChanged, formState]
  );
  const isDisabled = useMemo(
    () =>
      getDisabledFieldConfig({
        entitlement,
        formState,
        isBillingCycleChanged,
        isAnnualTermEnabled,
      }),
    [isBillingCycleChanged, formState]
  );
  const offeringOptions = useMemo(() => {
    let offerings = (availableOfferings.response?.[entitlement.id] as Offering[]) ?? [];

    if (isTrialEntitlement(entitlement, isStatusPageProduct)) {
      offerings = [...offerings, entitlement.offering as Offering];
    }

    return getOfferingOptions(offerings, !!entitlement.isSandbox);
  }, [availableOfferings]);
  const pricingPlanOptions = useMemo(
    () =>
      getPricingPlanOptions(
        availablePricingPlans.response?.[formState.billingFrequency]?.[entitlement.id] as PricingPlan[]
      ),
    [availablePricingPlans]
  );
  const userTierOptions = useMemo(() => {
    if (entitlement.isApp || isStatusPageProduct || formState.billingFrequency !== BillingFrequency.ANNUAL) {
      return undefined;
    }

    return getUserTierOptions({
      userTiers: formState.pricingPlan?.tiers,
    });
  }, [formState]);

  const [linkedEntitlementsSiblings, linkedEntitlementsApps] = useMemo(() => {
    const siblings: LinkedDestinationEntitlementWithDetails[] = [];
    const apps: LinkedDestinationEntitlementWithDetails[] = [];

    if (isBillingCycleChanged || formState.billingFrequency === BillingFrequency.ANNUAL) {
      filteredLinkedEntitlements?.forEach((linkedEntitlement) => {
        const linkedEntFormState = formState[linkedEntitlement.entitlementId];
        if (linkedEntFormState) {
          let offerings = (availableOfferings.response?.[linkedEntitlement.entitlementId] as Offering[]) ?? [];
          const isDisabled = getDisabledFieldConfig({
            entitlement: linkedEntitlement,
            formState: linkedEntFormState,
            isBillingCycleChanged,
            isAnnualTermEnabled,
          });
          const isHidden = getHiddenFieldConfig({
            entitlement: linkedEntitlement,
            formState: linkedEntFormState,
            isBillingCycleChanged,
          });
          let userTierOptions: UserTierOption[] | undefined;

          if (isTrialEntitlement(linkedEntitlement, isStatuspage(linkedEntitlement.productId))) {
            offerings = [...offerings, linkedEntitlement.offering as Offering];
          }

          if (formState.billingFrequency === BillingFrequency.ANNUAL && !linkedEntitlement.isApp) {
            userTierOptions = getUserTierOptions({
              userTiers: linkedEntFormState?.pricingPlan?.tiers,
            }) as UserTierOption[];
          }

          const item: LinkedDestinationEntitlementWithDetails = {
            ...linkedEntitlement,
            offeringOptions: getOfferingOptions(offerings, !!linkedEntitlement.isSandbox),
            pricingPlanOptions: getPricingPlanOptions(
              availablePricingPlans.response?.[formState.billingFrequency]?.[
                linkedEntitlement.entitlementId
              ] as PricingPlan[]
            ),
            userTierOptions,
            isDisabled,
            isHidden,
          };

          if (!item?.isApp) {
            siblings.push(item);
          } else {
            apps.push(item);
          }
        }
      });
    }

    return [siblings, apps];
  }, [
    filteredLinkedEntitlements,
    formState,
    availableOfferings,
    availablePricingPlans,
    isBillingCycleChanged,
    isAnnualBillingCycleChanged,
  ]);

  useEffect(() => {
    updateRefetchLoadingState({
      loading: {},
    });
    setIsFormDirty(null);
  }, []);

  const updateAppUserTier = ({
    formStateValues,
    entitlementId,
    isCurrentEntitlement,
    entitlement,
  }: {
    formStateValues: FormState;
    entitlementId: string;
    isCurrentEntitlement: boolean;
    entitlement: EntitlementDetails | LinkedDestinationEntitlement;
  }) => {
    const updatedState = {
      ...formState,
    };
    const updatedFormValues = {
      ...formStateValues,
    };
    const isStatusPageProduct = isStatuspage(entitlement.productId);
    const apps = filteredLinkedEntitlements?.filter((item) => !!item.isApp);

    if (
      !isStatusPageProduct &&
      !entitlement.isApp &&
      formState.billingFrequency === BillingFrequency.ANNUAL &&
      (apps?.length > 0 || !!entitlementDetails.isApp)
    ) {
      let maxUserTier: UserTierOption | null | undefined = updatedFormValues.userTier;
      let maxUserTiervalue: Maybe<number> | undefined = updatedFormValues.userTier?.value ?? 0;

      if (isBillingCycleChanged) {
        linkedEntitlementsSiblings?.forEach((item) => {
          const linkedEntUserTierValue = updatedState[item.entitlementId]?.userTier?.value ?? 0;
          //@ts-ignore
          if (item.entitlementId !== entitlementId && linkedEntUserTierValue > maxUserTiervalue) {
            maxUserTiervalue = linkedEntUserTierValue;
            maxUserTier = updatedState[item.entitlementId]?.userTier;
          }
        });

        if (!isCurrentEntitlement && !entitlementDetails.isApp) {
          const userTierValue = updatedState.userTier?.value ?? 0;

          if (userTierValue > maxUserTiervalue) {
            maxUserTiervalue = userTierValue;
            maxUserTier = updatedState.userTier;
          }
        }

        linkedEntitlementsApps.forEach((app) => {
          let userTier = maxUserTier;

          if (!maxUserTiervalue) {
            const userTierOptions = getUserTierOptions({
              activeUsers: app.activeUsers,
              userTiers: isCurrentEntitlement
                ? updatedFormValues[app.entitlementId]?.pricingPlan?.tiers
                : formState[app.entitlementId]?.pricingPlan?.tiers,
            }) as UserTierOption[];
            userTier = userTierOptions?.[0] ?? null;
          }

          if (isCurrentEntitlement) {
            updatedFormValues[app.entitlementId] = {
              ...updatedFormValues[app.entitlementId],
              userTier,
            };
          } else {
            updatedState[app.entitlementId] = {
              ...updatedState[app.entitlementId],
              userTier,
            };
          }
        });

        if (entitlementDetails.isApp) {
          updatedState.userTier = maxUserTier;

          if (!maxUserTiervalue) {
            const userTierOptions = getUserTierOptions({
              activeUsers: entitlementDetails.activeUsers,
              userTiers: formState.pricingPlan?.tiers,
            }) as UserTierOption[];

            updatedState.userTier = userTierOptions?.[0] ?? null;
          }
        }
      } else {
        linkedEntitlements
          ?.filter(
            (item) =>
              !item.isApp && item.billingStatus === BillingStatus.ACTIVE && item.billingFrequency && item.userTier
          )
          ?.forEach((linkedEntitlement) => {
            //@ts-ignore
            if (linkedEntitlement?.userTier > maxUserTiervalue) {
              maxUserTiervalue = linkedEntitlement.userTier;
              const userTierOptions = getUserTierOptions({
                chargeQuantity: maxUserTiervalue,
                userTiers: linkedEntitlement?.pricingPlan?.tiers,
              }) as UserTierOption[];
              maxUserTier = userTierOptions?.[0] ?? null;
            }
          });

        linkedEntitlementsApps.forEach((app) => {
          let userTier = maxUserTier;

          if (!maxUserTier) {
            const userTierOptions = getUserTierOptions({
              activeUsers: app.activeUsers,
              userTiers: updatedFormValues[app.entitlementId]?.pricingPlan?.tiers,
            }) as UserTierOption[];
            userTier = userTierOptions?.[0] ?? null;
          }

          updatedFormValues[app.entitlementId] = {
            ...updatedFormValues[app.entitlementId],
            userTier,
          };
        });
      }
    }

    return {
      updatedFormValues,
      updatedState,
    };
  };

  const handleFormElementChange = async ({
    fieldName,
    fieldValue,
    entitlement,
    linkedEntitlement,
  }: {
    fieldName: string;
    fieldValue: OnChangeFieldValue;
    entitlement?: EntitlementDetails;
    linkedEntitlement?: LinkedDestinationEntitlement;
  }) => {
    let entitlementId = entitlement?.id as string,
      isCurrentEntitlement = true,
      entitlementData = entitlement as EntitlementDetails | LinkedDestinationEntitlement,
      formStateValues = { ...formState };

    if (linkedEntitlement) {
      entitlementId = linkedEntitlement.entitlementId as string;
      isCurrentEntitlement = false;
      formStateValues = { ...formState[entitlementId] };
      entitlementData = linkedEntitlement;
    }

    const isAppEntitlement = !!entitlementData?.isApp;
    const isStatusPageProduct = isStatuspage(entitlementData?.productId);

    switch (fieldName) {
      case FORM_NAMES.CUSTOM_PROMOTION: {
        if (isCurrentEntitlement) {
          handleCustomPromotionValueChange(fieldValue);
        }
        break;
      }
      case FORM_NAMES.SKIP_TRIAL: {
        const updatedStateWithRemovedPromotions = getFormStateWithoutAddedPromotionFields(formStateValues);
        updatedStateWithRemovedPromotions.skipTrial = fieldValue as boolean;
        updatedStateWithRemovedPromotions.hasOptedForUpgradeTrial = isOptedForUpgradeTrial({
          entitlement: entitlementData,
          formState: updatedStateWithRemovedPromotions,
        });

        if (isCurrentEntitlement) {
          updateFormStateValues(updatedStateWithRemovedPromotions);
        } else {
          updateFormState({
            billingFrequency: formState.billingFrequency,
            [entitlementId]: updatedStateWithRemovedPromotions,
          });
        }

        setAvailablePromotions(entitlementData.entitlementNumber, fieldValue as boolean);
        break;
      }
      case FORM_NAMES.OFFERING: {
        updateRefetchLoadingState({
          loading: {
            ...refetchLoadingState.loading,
            [entitlementId]: true,
          },
        });
        formStateValues.offering = fieldValue as OfferingOption;
        const defaultAPIRequest: any = {
          entitlement: entitlementData,
          entitlementId: entitlementId,
          offeringId: formStateValues.offering?.value,
          billingCycle: formState.billingFrequency,
        };
        const pricingPlanRequest: any = {
          entitlementNumber: entitlementData.entitlementNumber,
          currency: defaultTo(currentInvoiceGroup?.currency, Currency.USD),
          offeringId: formStateValues.offering?.value,
          planType: pricingEligibility,
          billingCycle: formState.billingFrequency,
        };
        const promises = [fetchPricingPlans(pricingPlanRequest)];

        if (
          (isBillingCycleChanged || isCurrentEntitlement) &&
          !defaultApiData?.response?.[formState.billingFrequency]?.[formStateValues.offering?.value as string]
        ) {
          //@ts-ignore
          promises.push(getDefaultAPIEntitlementData(defaultAPIRequest));
        }

        try {
          const result: any = await Promise.all(promises);
          const pricingPlansResponse = result[0] as ApolloQueryResult<FetchAvailablePricingPlansQuery>;
          const defaultAPIResponse =
            promises.length > 1
              ? (result[1] as DefaultAPIResponse)
              : defaultApiData?.response?.[formState.billingFrequency]?.[formStateValues.offering?.value as string];
          let pricingPlans = pricingPlansResponse?.data?.entitlementDetails?.availablePricingPlans;
          pricingPlans = pricingPlans?.filter(
            (pricingPlan) => !notActivePricingStatus.includes(pricingPlan?.status as string)
          );
          const defaultPricingPlanId = defaultAPIResponse?.pricingPlanId?.value;
          const defaultPricingPlan =
            isBillingCycleChanged && defaultPricingPlanId
              ? pricingPlans?.find((item) => item?.id === defaultPricingPlanId)
              : getDefaultPricingPlan(pricingPlans);
          const pricingPlanOption = defaultPricingPlan?.id
            ? {
                label: getPricingPlanDescription(defaultPricingPlan),
                value: defaultPricingPlan.id,
                tiers: defaultPricingPlan.tiers,
                chargeElement: defaultPricingPlan.chargeElement,
                chargeType: defaultPricingPlan.chargeType,
                type: defaultPricingPlan.type,
                pricingPlanType: defaultPricingPlan.pricingPlanType,
              }
            : null;
          let allowTierChange;

          if (!isStatusPageProduct && !isAppEntitlement && formState.billingFrequency === BillingFrequency.ANNUAL) {
            const userTierOptions = getUserTierOptions({
              chargeQuantity: formStateValues.userTier?.value,
              activeUsers: entitlementData?.activeUsers,
              userTiers: defaultPricingPlan?.tiers,
            }) as UserTierOption[];

            formStateValues.userTier = userTierOptions?.[0] ?? null;

            allowTierChange = isTierChangeAllowed({
              activeUsers: entitlementData.activeUsers,
              userTier: formStateValues.userTier?.value,
            });
          }

          formStateValues.pricingPlan = pricingPlanOption;
          const skipTrialData = evaluateOffering({
            entitlement: entitlementData,
            selectedOffering: formStateValues.offering,
            isBillingCycleChanged,
            isAnnualTermEnabled,
          });
          let skipTrialValueChanged = false;

          if (!isNil(skipTrialData?.skipTrial)) {
            if (skipTrialData?.skipTrial !== formStateValues.skipTrial) {
              formStateValues = getFormStateWithoutAddedPromotionFields(formStateValues);
              skipTrialValueChanged = true;
            }

            formStateValues = {
              ...formStateValues,
              ...skipTrialData,
            };
          }

          formStateValues.hasOptedForUpgradeTrial = isOptedForUpgradeTrial({
            entitlement: entitlementData,
            formState: formStateValues,
          });
          const finalStateValues = updateAppUserTier({
            formStateValues,
            entitlementId,
            isCurrentEntitlement,
            entitlement: entitlementData,
          });

          if (isCurrentEntitlement) {
            if (!isBillingCycleChanged) {
              finalStateValues.updatedFormValues.nextBillingAnchorTimestamp =
                defaultAPIResponse?.billingAnchorTime?.value;
              updateDefaultApiData({
                ...defaultApiData,
                //@ts-ignore
                response: {
                  ...defaultApiData?.response,
                  [formState.billingFrequency]: {
                    ...defaultApiData?.response?.[formState.billingFrequency],
                    [formStateValues.offering?.value as string]: defaultAPIResponse,
                  },
                },
              });
            }

            updateFormStateValues(cloneDeep(finalStateValues.updatedFormValues));
          } else {
            updateFormState({
              ...finalStateValues.updatedState,
              [entitlementId]: finalStateValues.updatedFormValues,
            });
          }

          if (skipTrialValueChanged) {
            setAvailablePromotions(entitlementData.entitlementNumber, skipTrialData?.skipTrial);
          }

          !isNil(allowTierChange) &&
            setTierValidationState({
              ...tierValidationState,
              [entitlementId]: {
                allowed: allowTierChange,
                productId: entitlementData?.productId,
                activeUsers: entitlementData?.activeUsers,
              },
            });

          updateAvailablePricingPlans({
            ...availablePricingPlans,
            response: {
              ...availablePricingPlans?.response,
              [formState.billingFrequency]: {
                ...availablePricingPlans?.response?.[formState.billingFrequency],
                [entitlementId]: pricingPlansResponse?.data?.entitlementDetails?.availablePricingPlans,
              },
            },
          });

          updateRefetchLoadingState({
            loading: {
              ...refetchLoadingState.loading,
              [entitlementId]: false,
            },
          });
        } catch (e) {
          updateRefetchLoadingState({
            loading: {
              ...refetchLoadingState.loading,
              [entitlementId]: false,
            },
          });
        }

        break;
      }
      case FORM_NAMES.USER_TIER: {
        formStateValues.userTier = fieldValue as UserTierOption;
        const allowTierChange = isTierChangeAllowed({
          activeUsers: entitlementData.activeUsers,
          userTier: formStateValues.userTier?.value,
        });
        const finalStateValues = updateAppUserTier({
          formStateValues,
          entitlementId,
          isCurrentEntitlement,
          entitlement: entitlementData,
        });

        if (isCurrentEntitlement) {
          updateFormState(finalStateValues.updatedFormValues);
        } else {
          updateFormState({
            ...finalStateValues.updatedState,
            [entitlementId]: finalStateValues.updatedFormValues,
          });
        }

        setTierValidationState({
          ...tierValidationState,
          [entitlementId]: {
            allowed: allowTierChange,
            productId: entitlementData?.productId,
            activeUsers: entitlementData?.activeUsers,
          },
        });

        break;
      }
      case FORM_NAMES.PRICING_PLAN: {
        let allowTierChange;
        formStateValues.pricingPlan = fieldValue as PricingPlanOption;
        if (!isStatusPageProduct && !isAppEntitlement && formState.billingFrequency === BillingFrequency.ANNUAL) {
          const userTierOptions = getUserTierOptions({
            chargeQuantity: formStateValues?.userTier?.value,
            userTiers: formStateValues.pricingPlan?.tiers,
          }) as UserTierOption[];

          formStateValues.userTier = userTierOptions?.[0] ?? null;

          allowTierChange = isTierChangeAllowed({
            activeUsers: entitlementData.activeUsers,
            userTier: formStateValues.userTier?.value,
          });
        }

        const finalStateValues = updateAppUserTier({
          formStateValues,
          entitlementId,
          isCurrentEntitlement,
          entitlement: entitlementData,
        });

        if (isCurrentEntitlement) {
          updateFormState(finalStateValues.updatedFormValues);
        } else {
          updateFormState({
            ...finalStateValues.updatedState,
            [entitlementId]: finalStateValues.updatedFormValues,
          });
        }

        !isNil(allowTierChange) &&
          setTierValidationState({
            ...tierValidationState,
            [entitlementId]: {
              allowed: allowTierChange,
              productId: entitlementData?.productId,
              activeUsers: entitlementData?.activeUsers,
            },
          });

        break;
      }

      default:
        break;
    }
  };

  const onEntitlementChange = (entitlement: EntitlementDetails, fieldName: string, fieldValue: OnChangeFieldValue) => {
    handleFormElementChange({ fieldName, fieldValue, entitlement });
  };

  const onLinkedEntitlementChange = (
    linkedEntitlement: LinkedDestinationEntitlement,
    fieldNameWithId: string,
    fieldValue: OnChangeFieldValue
  ) => {
    const fieldName = fieldNameWithId.split('.')?.[1];
    handleFormElementChange({ fieldName, fieldValue, linkedEntitlement });
  };

  return (
    <div className="plan-details">
      <h2>Select plans and discounts</h2>
      <ManageSubscriptionPanel
        isExpanded={true}
        showLoader={refetchLoadingState?.loading?.[entitlement.id]}
        entitlement={entitlement}
        offeringOptions={offeringOptions}
        pricingPlanOptions={pricingPlanOptions}
        userTierOptions={userTierOptions as UserTierOption[]}
        selectedUserTier={formState.userTier}
        selectedPricingPlan={formState.pricingPlan}
        selectedOffering={formState.offering}
        billingFrequency={formState.billingFrequency}
        isSkipTrialChecked={formState.skipTrial}
        availablePromotions={availablePromotionsData?.response?.[entitlement?.entitlementNumber || ''] || []}
        partnerPromotion={partnerPromotionData?.response?.[entitlement?.entitlementNumber as string] || null}
        selectedPromotionsToAdd={pickPromotionFields(formState)}
        selectedPromotionsToRemove={formState.promotionsToRemove}
        selectedSchedulePromotionsToRemove={formState.schedulePromotionsToRemove}
        isDisabled={isDisabled}
        selectedUnits={isStatusPageProduct ? formState.units : undefined}
        hasOptedForUpgradeTrial={formState.hasOptedForUpgradeTrial}
        onChange={(...params) => onEntitlementChange(entitlement, ...params)}
        isLoading={{
          addPromotions:
            !!availablePromotionsData?.loading?.ALL ||
            !!availablePromotionsData?.loading?.[entitlement.entitlementNumber as string] ||
            !!partnerPromotionData?.loading?.ALL ||
            !!partnerPromotionData?.loading?.[entitlement.entitlementNumber as string],
        }}
        isHidden={isHidden}
      />
      {(isBillingCycleChanged || formState.billingFrequency === BillingFrequency.ANNUAL) &&
        filteredLinkedEntitlements?.length > 0 && (
          <>
            <h3>Linked subscriptions</h3>
            <p className="linked-subscriptions-subheading">Expand an item below to view or edit.</p>
            {linkedEntitlementsSiblings?.map((linkedEntitlement) => {
              const linkedEntFormState = formState[linkedEntitlement.entitlementId];

              return (
                <ManageSubscriptionPanel
                  showLoader={refetchLoadingState?.loading?.[linkedEntitlement.entitlementId]}
                  fieldName={linkedEntitlement.entitlementId}
                  key={linkedEntitlement.entitlementId}
                  entitlement={linkedEntitlement}
                  offeringOptions={linkedEntitlement.offeringOptions}
                  pricingPlanOptions={linkedEntitlement.pricingPlanOptions}
                  userTierOptions={linkedEntitlement.userTierOptions}
                  selectedUserTier={linkedEntFormState?.userTier}
                  selectedPricingPlan={linkedEntFormState?.pricingPlan}
                  hasOptedForUpgradeTrial={linkedEntFormState?.hasOptedForUpgradeTrial}
                  selectedOffering={linkedEntFormState.offering}
                  isSkipTrialChecked={linkedEntFormState?.skipTrial}
                  billingFrequency={formState.billingFrequency}
                  availablePromotions={
                    availablePromotionsData?.response?.[linkedEntitlement?.entitlementNumber || ''] || []
                  }
                  partnerPromotion={
                    partnerPromotionData?.response?.[linkedEntitlement?.entitlementNumber as string] || null
                  }
                  selectedPromotionsToAdd={pickPromotionFields(linkedEntFormState)}
                  selectedPromotionsToRemove={linkedEntFormState?.promotionsToRemove}
                  selectedSchedulePromotionsToRemove={linkedEntFormState?.schedulePromotionsToRemove}
                  isDisabled={linkedEntitlement.isDisabled}
                  onChange={(...params) => onLinkedEntitlementChange(linkedEntitlement, ...params)}
                  isLoading={{
                    addPromotions:
                      !!availablePromotionsData?.loading?.ALL ||
                      !!availablePromotionsData?.loading?.[linkedEntitlement.entitlementNumber as string] ||
                      !!partnerPromotionData?.loading?.ALL ||
                      !!partnerPromotionData?.loading?.[linkedEntitlement.entitlementNumber as string],
                  }}
                  isHidden={linkedEntitlement.isHidden}
                />
              );
            })}
            {linkedEntitlementsApps?.length > 0 && (
              <>
                <h4>{`Apps for ${entitlement?.productName?.split(' ')?.[0]}`}</h4>
                {linkedEntitlementsApps?.map((linkedEntitlement) => {
                  const linkedEntFormState = formState[linkedEntitlement.entitlementId];

                  return (
                    <ManageSubscriptionPanel
                      showLoader={refetchLoadingState?.loading?.[linkedEntitlement.entitlementId]}
                      fieldName={linkedEntitlement.entitlementId}
                      key={linkedEntitlement.entitlementId}
                      entitlement={linkedEntitlement}
                      offeringOptions={linkedEntitlement.offeringOptions}
                      pricingPlanOptions={linkedEntitlement.pricingPlanOptions}
                      userTierOptions={linkedEntitlement.userTierOptions}
                      selectedUserTier={linkedEntFormState?.userTier}
                      selectedPricingPlan={linkedEntFormState?.pricingPlan}
                      selectedOffering={linkedEntFormState.offering}
                      isSkipTrialChecked={linkedEntFormState?.skipTrial}
                      billingFrequency={formState.billingFrequency}
                      hasOptedForUpgradeTrial={linkedEntFormState?.hasOptedForUpgradeTrial}
                      availablePromotions={
                        availablePromotionsData?.response?.[linkedEntitlement?.entitlementNumber || ''] || []
                      }
                      partnerPromotion={
                        partnerPromotionData?.response?.[linkedEntitlement?.entitlementNumber as string] || null
                      }
                      selectedPromotionsToAdd={pickPromotionFields(linkedEntFormState)}
                      selectedPromotionsToRemove={linkedEntFormState?.promotionsToRemove}
                      selectedSchedulePromotionsToRemove={linkedEntFormState?.schedulePromotionsToRemove}
                      isDisabled={linkedEntitlement.isDisabled}
                      onChange={(...params) => onLinkedEntitlementChange(linkedEntitlement, ...params)}
                      isLoading={{
                        addPromotions:
                          !!availablePromotionsData?.loading?.ALL ||
                          !!availablePromotionsData?.loading?.[linkedEntitlement.entitlementNumber as string] ||
                          !!partnerPromotionData?.loading?.ALL ||
                          !!partnerPromotionData?.loading?.[linkedEntitlement.entitlementNumber as string],
                      }}
                      isHidden={linkedEntitlement.isHidden}
                    />
                  );
                })}
              </>
            )}
          </>
        )}
    </div>
  );
};
